/**
 * SMS Opt-In Disclosure page — /sms-opt-in (WS8-B compliance).
 *
 * Exists for A2P 10DLC campaign vetting (Twilio error 30896): the real opt-in
 * screen lives behind a login at /crew/profile, so reviewers cannot see it.
 * This public page documents the entire consent flow and embeds faithful
 * renderings of the in-app screens — the same markup and styling the app
 * itself uses — serving as the "hosted screenshots" the campaign's
 * message_flow references.
 *
 * Constraints:
 *  - Registered under LegalLayout and PRERENDERED to static HTML (see
 *    scripts/prerender-legal.mjs) — reviewers may fetch without running JS,
 *    so this component must not depend on react-router or browser APIs.
 *  - The consent wording quoted here is SMS_CONSENT_TEXT from config/legal.ts
 *    — the same constant the app renders and stores on the user record — so
 *    the page can never drift from what users actually agree to.
 *  - Keep the URL stable: it is registered with The Campaign Registry.
 */

import { SMS_CONSENT_TEXT, LEGAL_ENTITY, CONTACT_SUPPORT } from '../../config/legal';

/**
 * Live screenshots of the actual in-app screens, captured from the running
 * application (Chromium, 2x scale). Regenerate whenever the consent UI
 * changes: the screenshots must always match what users really see.
 * Served from /screenshots/ — the same URLs are cited directly in the A2P
 * campaign's message_flow field, per Twilio's onboarding guide requirement
 * to provide "a publicly accessible link to a screenshot" for opt-in flows
 * behind a login.
 */
function Screenshot({ src, alt, caption }: { src: string; alt: string; caption: string }) {
  return (
    <figure className="not-prose my-6">
      <a href={src} target="_blank" rel="noreferrer">
        <img
          src={src}
          alt={alt}
          className="w-full max-w-md mx-auto rounded-lg border border-gray-300 shadow-sm"
        />
      </a>
      <figcaption className="text-center text-xs text-gray-500 mt-2">{caption}</figcaption>
    </figure>
  );
}

export default function SmsOptInPage() {
  return (
    <>
      <h1>SMS Opt-In &amp; Messaging Disclosure</h1>
      <p>
        <strong>Last updated:</strong> August 10, 2026
      </p>
      <p>
        ProgramRow (operated by {LEGAL_ENTITY}) is a crew-scheduling platform for school,
        college, and video-production broadcast crews. Crew members can optionally receive text
        messages about their own crew assignments and schedule changes. This page documents
        exactly how that consent is collected, because the opt-in screen itself is inside the
        application behind a sign-in.
      </p>

      <h2>How a crew member opts in</h2>
      <ol>
        <li>
          The crew member signs in to their own ProgramRow account and opens{' '}
          <strong>Profile → Text Message Notifications</strong>. Texting is <strong>optional</strong>;
          every feature of the service works without it, and email notifications remain available
          either way.
        </li>
        <li>
          They enter <strong>their own</strong> mobile number. Organization administrators cannot
          enter a number here or enable text messaging on anyone's behalf. A phone number an administrator
          has on file is stored as contact information only and is never texted.
        </li>
        <li>
          They check a consent checkbox that is <strong>unchecked by default</strong>, separate
          from every other agreement, with the full disclosure visible (shown below).
        </li>
        <li>
          ProgramRow sends a one-time passcode to the number. The number is not eligible for any
          notification until the crew member enters the code, proving the person consenting holds
          the handset.
        </li>
        <li>
          On verification, ProgramRow stores the consent timestamp, the originating IP address,
          and the version of the disclosure text displayed, on that user's record, and sends the confirmation
          message shown below.
        </li>
      </ol>

      <h2>The opt-in screen: live screenshots</h2>
      <p>
        These are unaltered screenshots of the in-app screen at Profile → Text Message
        Notifications (the live screen requires a signed-in crew account). Note the consent
        checkbox is unchecked by default, the submit button is disabled until consent is given,
        and the Privacy Policy and Terms of Service are linked directly within the disclosure,
        adjacent to the checkbox. Direct image links:{' '}
        <a href="/screenshots/sms-opt-in-blank.png">before opt-in</a> ·{' '}
        <a href="/screenshots/sms-opt-in-completed.png">form completed</a>.
      </p>
      <Screenshot
        src="/screenshots/sms-opt-in-blank.png"
        alt="ProgramRow SMS opt-in screen: optional mobile number field, unchecked consent checkbox with the full disclosure and links to the Privacy Policy and Terms of Service, disabled submit button"
        caption="The opt-in screen as first presented: checkbox unchecked, submit disabled until consent is given."
      />
      <Screenshot
        src="/screenshots/sms-opt-in-completed.png"
        alt="The same screen with a number entered and the consent checkbox checked by the user"
        caption="The form completed by the user, with the number entered and consent checked. Submitting sends the one-time passcode."
      />
      <p>The consent disclosure next to the checkbox reads, verbatim:</p>
      <blockquote>
        <p>{SMS_CONSENT_TEXT}</p>
      </blockquote>

      <h2>Handset verification</h2>
      <p>
        On submit, ProgramRow texts a 6-digit one-time passcode to the number via Twilio
        Verify. The screen prompts "Enter the 6-digit code we texted to (•••) •••-01XX" with a
        code field and a "Verify and turn on" button. The number cannot receive any campaign
        message until the code is entered, so the person consenting is provably the person
        holding the handset.
      </p>

      <h2>Messages we send after opt-in</h2>
      <p>
        Only account-notification messages about events the crew member is personally involved
        in: new crew assignments, call-time or venue changes, event cancellations, and
        operational messages from their organization's scheduling administrator. No marketing,
        promotional, or third-party content. Phone numbers are never sold or shared for
        marketing by anyone, in any circumstance.
      </p>
      <p>
        <strong>Opt-in confirmation message:</strong>
      </p>
      <blockquote>
        <p>
          ProgramRow: You're signed up for crew assignment alerts. Msg frequency varies. Msg
          &amp; data rates may apply. Reply HELP for help, STOP to cancel.
        </p>
      </blockquote>

      <h2>Opting out</h2>
      <ul>
        <li>
          Reply <strong>STOP</strong> (or STOPALL, UNSUBSCRIBE, CANCEL, END, QUIT) to any
          message. Confirmation reply: <em>"You have been unsubscribed from ProgramRow crew
          notifications and will receive no further messages. Reply START to resubscribe."</em>
        </li>
        <li>
          Or turn off text messages in <strong>Profile → Text Message Notifications</strong> at
          any time.
        </li>
        <li>
          Reply <strong>HELP</strong> (or INFO) for help: <em>"ProgramRow crew scheduling
          alerts. Support: support@programrow.com or https://programrow.com/docs. Msg &amp; data
          rates may apply. Reply STOP to opt out."</em>
        </li>
      </ul>
      <p>
        Opting out never affects the account, assignments, or any other part of the service.
      </p>

      <h2>Rates and frequency</h2>
      <p>
        Message frequency varies with how often the crew member is assigned to events. Message
        and data rates may apply. Carriers are not liable for delayed or undelivered messages.
      </p>

      <h2>Related policies</h2>
      <p>
        <a href="/privacy">Privacy Policy</a> · <a href="/terms">Terms of Service</a> ·
        questions: <a href={`mailto:${CONTACT_SUPPORT}`}>{CONTACT_SUPPORT}</a>
      </p>
    </>
  );
}
